import React, { useState } from 'react';
import { PhoneAuthForm } from './PhoneAuthForm';
import { TokenVerificationForm } from './TokenVerificationForm';
import { useAuth } from '../../hooks/useAuth';
import { Loader } from '../common';

interface AuthContainerProps {
  onAuthSuccess?: () => void;
}

type AuthStep = 'phone' | 'token' | 'success';

export const AuthContainer: React.FC<AuthContainerProps> = ({ onAuthSuccess }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState<AuthStep>('phone');
  const [phone, setPhone] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  // Si l'utilisateur est déjà authentifié, on affiche un message de succès
  if (isAuthenticated) {
    return (
      <div className="w-full max-w-md mx-auto bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-4">
        <div className="text-center">
          <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h2 className="text-2xl font-bold mb-2 text-gray-800">Vous êtes connecté</h2>
          <p className="text-gray-600 mb-4">Vous pouvez maintenant accéder à toutes les fonctionnalités.</p>
        </div>
      </div>
    );
  }

  // Si l'authentification est en cours de chargement, on affiche un loader
  if (isLoading) {
    return (
      <div className="w-full max-w-md mx-auto flex justify-center items-center py-12">
        <Loader size="large" color="primary" text="Chargement..." />
      </div>
    );
  }

  // Gestion de la réception du numéro de téléphone
  const handlePhoneSuccess = (phoneNumber: string, tokenExpiresAt: string) => {
    setPhone(phoneNumber);
    setExpiresAt(tokenExpiresAt);
    setCurrentStep('token');
  };

  // Gestion de la vérification réussie du token
  const handleTokenSuccess = () => {
    setCurrentStep('success');
    if (onAuthSuccess) {
      onAuthSuccess();
    }
  };

  // Retour à l'étape de saisie du numéro de téléphone
  const handleBackToPhone = () => {
    setCurrentStep('phone');
  };

  // Affichage du composant en fonction de l'étape actuelle
  const renderStep = () => {
    switch (currentStep) {
      case 'phone':
        return <PhoneAuthForm onSuccess={handlePhoneSuccess} />;
      case 'token':
        return (
          <TokenVerificationForm 
            phone={phone} 
            expiresAt={expiresAt} 
            onSuccess={handleTokenSuccess} 
            onBack={handleBackToPhone} 
          />
        );
      case 'success':
        return (
          <div className="w-full max-w-md mx-auto bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-4">
            <div className="text-center">
              <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <h2 className="text-2xl font-bold mb-2 text-gray-800">Authentification réussie!</h2>
              <p className="text-gray-600 mb-4">Vous êtes maintenant connecté.</p>
            </div>
          </div>
        );
      default:
        return <PhoneAuthForm onSuccess={handlePhoneSuccess} />;
    }
  };

  return (
    <div className="w-full">
      {renderStep()}
    </div>
  );
};
